import { Component } from '@theme/component';
import { QuantitySelectorUpdateEvent } from '@theme/events';

/**
 * A custom element that allows the user to select a quantity.
 *
 * @typedef {Object} Refs
 * @property {HTMLInputElement} quantityInput
 * @property {HTMLElement} [errorMessage]
 *
 * @extends {Component<Refs>}
 */
class QuantitySelectorComponent extends Component {
  connectedCallback() {
    super.connectedCallback?.();

    // Listen for sync events globally
    window.addEventListener('quantity:sync', (event) => {
      const { variantId, quantity } = event.detail;
      const input = this.refs.quantityInput;
      if (parseInt(input.dataset.variantId, 10) === variantId) {
        if(quantity > 0){
          input.value = quantity;
        }
        else{
          input.value = 1;
        }
      }
    });
  }

  increaseQuantity(event) {
    if (!(event.target instanceof HTMLElement)) return;
    event.preventDefault();
    const input = this.refs.quantityInput;
    const newQty = (parseInt(input.value, 10) || 0) + 1;

    this.#applyQuantity(newQty);
  }

  decreaseQuantity(event) {
    if (!(event.target instanceof HTMLElement)) return;
    event.preventDefault();
    const input = this.refs.quantityInput;
    const newQty = Math.max((parseInt(input.value, 10) || 1) - 1, 1);

    this.#applyQuantity(newQty);
  }

  setQuantity(event) {
    if (!(event.target instanceof HTMLInputElement)) return;
    event.preventDefault();
    const newQty = parseInt(event.target.value, 10) || 1;

    this.#applyQuantity(newQty);
  }

  selectInputValue(event) {
    const { quantityInput } = this.refs;
    if (event.target instanceof HTMLInputElement && document.activeElement === quantityInput) {
      quantityInput.select();
    }
  }

  #applyQuantity(newQty) {
    const input = this.refs.quantityInput;
    input.value = newQty;

    this.#validateQuantity(newQty);

    // 🔥 Fire sync event so all selectors update instantly
    const variantId = parseInt(input.dataset.variantId, 10);
    window.dispatchEvent(
      new CustomEvent('quantity:sync', {
        detail: { variantId, quantity: newQty },
      }),
    );
  }

  #validateQuantity(newQty) {
    if (newQty < 1){
      newQty = 1;
    }
    const input = this.refs.quantityInput;
    const errorEl = this.refs.errorMessage;
    const productHandle = this.dataset.handle;
    const variantId = parseInt(input.dataset.variantId, 10);
    const cartLine = Number(input.dataset.cartLine || 0);

    const promoBanners = document.querySelectorAll(`.promo-banner[data-variant-id="${variantId}"]`);
    const discountWidget = document.querySelector(`.quantity-discount[data-variant-id="${variantId}"]`);
    const nextTier = [...(discountWidget?.querySelectorAll('.quantity-discount__option') ?? [])]
      .map((button) => ({
        quantity: Number(button.dataset.quantity),
        discount: Number(button.dataset.discount),
      }))
      .find((tier) => tier.quantity > newQty);

    promoBanners.forEach((promoBanner) => {
      const nextQtyEl = promoBanner.querySelector('.promo-banner__next-qty');
      const nextDisEl = promoBanner.querySelector('.promo-banner__next-dis');
      const nextPriceEl = promoBanner.querySelector('.promo-banner__next-price');
      if (!nextQtyEl || !nextDisEl) return;

      promoBanner.hidden = !nextTier;
      if (nextTier) {
        nextQtyEl.textContent = nextTier.quantity - newQty;
        nextDisEl.textContent = nextTier.discount;
        if (nextPriceEl) {
          const price = Number(discountWidget.dataset.price);
          nextPriceEl.dataset.discount = nextTier.discount;
          nextPriceEl.textContent = `$${(price * (1 - nextTier.discount / 100) / 100).toFixed(2)}`;
        }
      }
    });

    fetch(`/products/${productHandle}.js`)
      .then((res) => res.json())
      .then((product) => {
        const variant = product.variants.find((v) => v.id === variantId);
        if (!variant) return;

        const availableQty = variant.inventory_quantity;
        if (newQty > availableQty) {
          document
            .querySelectorAll(`input[name="quantity"][data-variant-id="${variantId}"]`)
            .forEach((el) => (el.value = availableQty));
          if (errorEl) {
            errorEl.textContent = `Only ${availableQty} in stock.`;
            errorEl.hidden = false;
          }
        } else {
          if (errorEl) errorEl.hidden = true;
          input.dispatchEvent(new QuantitySelectorUpdateEvent(newQty, cartLine));
        }
      });
  }
}

if (!customElements.get('quantity-selector-component')) {
  customElements.define('quantity-selector-component', QuantitySelectorComponent);
}
